import type { CSSProperties } from "react";

/**
 * Visual presets for an erasure.
 *
 * `marked` / `unmarked` are spread onto each word's span, so they must be valid
 * inline styles. The three presets are the three ways erasure poetry is
 * conventionally presented: faded out, blacked out, or blacked out with the
 * erased text still faintly legible underneath.
 */
export interface StyleConfig {
  content: CSSProperties;
  editor: CSSProperties;
  text: {
    global: {
      fontSize: number;
      fontFamily: string;
      lineHeight: string;
    };
    marked: CSSProperties;
    unmarked: CSSProperties;
  };
}

const editorLayout: CSSProperties = {
  textAlign: "left",
  wordWrap: "break-word",
  cursor: "pointer",
  userSelect: "none",
};

const globalText = {
  fontSize: 15,
  fontFamily: "Helvetica, Arial",
  lineHeight: "1em",
} as const;

export const defaultStyle: StyleConfig = {
  content: { backgroundColor: "white", padding: "12%" },
  editor: editorLayout,
  text: {
    global: { ...globalText },
    marked: { opacity: "10%" },
    unmarked: { opacity: "100%" },
  },
};

export const blackoutStyle: StyleConfig = {
  content: { backgroundColor: "black", padding: "12%" },
  editor: editorLayout,
  text: {
    global: { ...globalText },
    marked: { backgroundColor: "black", opacity: "100%" },
    unmarked: { backgroundColor: "white", opacity: "100%" },
  },
};

export const partialBlackoutStyle: StyleConfig = {
  content: { ...blackoutStyle.content },
  editor: editorLayout,
  text: {
    global: { ...globalText },
    marked: { ...blackoutStyle.text.marked, color: "#ffffff87" },
    unmarked: { ...blackoutStyle.text.unmarked },
  },
};

export const styles = {
  defaultStyle,
  blackoutStyle,
  partialBlackoutStyle,
} as const;

export type StyleKey = keyof typeof styles;

export const styleKeys = Object.keys(styles) as StyleKey[];

/** The order the style button cycles through. */
export const nextStyleKey = (current: StyleKey): StyleKey =>
  styleKeys[(styleKeys.indexOf(current) + 1) % styleKeys.length];

export const MIN_FONT_SIZE = 8;
export const MAX_FONT_SIZE = 96;

/** Returns a fresh, mutable copy of a preset so callers never alias the module constant. */
export const cloneStyle = (key: StyleKey): StyleConfig => ({
  content: { ...styles[key].content },
  editor: { ...styles[key].editor },
  text: {
    global: { ...styles[key].text.global },
    marked: { ...styles[key].text.marked },
    unmarked: { ...styles[key].text.unmarked },
  },
});
